import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import "./TableStyle.css";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import Scrollbars from "react-custom-scrollbars-2";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import classes from "../SentimentBiggestChangePercentWidget/SentimentChangePercent.module.css";
import { useSelector } from "react-redux";

export const ShareOfVoiceOverTimeBubble = (props) => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [minValue, setMinValue] = useState(0);
    const [maxValue, setMaxValue] = useState(100); // Default range

    const [getVolume] = useGetConceptsFiltersMutation();

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey] || 14;
    });

    const {
        selectedDateName,
        interval,
        id,
        filters,
        conceptList,
        setFilterDocCount,
        setLocalConceptOptions,
    } = props;

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props],
    );

    const getVolumeData = useCallback(async () => {
        setLoading(true);

        const { data: dataConcepts } = await getVolume({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            breakdowns: [{ name: selectedDateName, interval }],
        });

        setFilterDocCount(dataConcepts.filter_count);

        if (conceptList) {
            setLocalConceptOptions(dataConcepts.match_counts);
        }

        const breakdownData = dataConcepts.breakdowns[0].buckets;
        const formattedData = [];

        breakdownData.forEach(({ label, match_counts, total_count,filter_count }) => {
            const formattedDate = formatDateToUtc(label, interval);
            match_counts.forEach(({ name, match_count }) => {
                const percentage = filter_count > 0 && match_count !== undefined && match_count !== null
                    ? ((match_count / filter_count) * 100).toFixed(1)
                    : 0;

                formattedData.push({
                    date: formattedDate,
                    category: name,
                    count: percentage,
                });
            });
        });


        setChartData(formattedData);

        const counts = formattedData.map(item => parseFloat(item.count));
        const minValue = Math.min(...counts);
        const maxValue = Math.max(...counts);
        setMinValue(minValue);
        setMaxValue(maxValue);

        setLoading(false);
    }, [
        conceptFilter,
        conceptList,
        filters,
        getVolume,
        id,
        interval,
        selectedDateName,
        setFilterDocCount,
        setLocalConceptOptions,
        props.seletctedType,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetVolume = debounce(getVolumeData, 1000);
        debouncedGetVolume();
        return () => {
            debouncedGetVolume.cancel();
        };
    }, [getVolumeData]);

    const dates = [...new Set(chartData.map(item => item.date))];
    const categories = [...new Set(chartData.map(item => item.category))];

    const getCountForCell = (date, category) => {
        const item = chartData.find(
            (d) => d.date === date && d.category === category,
        );

        if (!item || isNaN(item.count)) {
            return 0;
        }

        return parseFloat(item.count);
    };

    const adjustBrightness = (color, amount) => {
        let usePound = false;

        if (color[0] === "#") {
            color = color.slice(1);
            usePound = true;
        }

        let num = parseInt(color, 16);

        let r = (num >> 16) + amount;
        let g = ((num >> 8) & 0x00FF) + amount;
        let b = (num & 0x0000FF) + amount;

        if (r > 255) r = 255;
        else if (r < 0) r = 0;

        if (g > 255) g = 255;
        else if (g < 0) g = 0;

        if (b > 255) b = 255;
        else if (b < 0) b = 0;

        const newColor = (r << 16) | (g << 8) | b;
        return (usePound ? "#" : "") + newColor.toString(16).padStart(6, "0");
    };

    const scaleValue = (value, min, max, newMin, newMax) => {
        if (max === min) return newMin;
        return ((value - min) / (max - min)) * (newMax - newMin) + newMin;
    };
    
    const gradientColors = ["#ffffff", "#b2ddf1", "#7ac9ee", "#78A9FF", "#78A9FF"];

    const interpolateColor = (color1, color2, factor) => {
        const hex = (color) => color.substring(1).match(/.{1,2}/g).map((c) => parseInt(c, 16));
        
        let [r1, g1, b1] = hex(color1);
        let [r2, g2, b2] = hex(color2);
    
        const r = Math.round(r1 + factor * (r2 - r1));
        const g = Math.round(g1 + factor * (g2 - g1));
        const b = Math.round(b1 + factor * (b2 - b1));
    
        return `rgb(${r}, ${g}, ${b})`;
    };
    
    const getColorForCount = (count) => {
        if (count === null || count === undefined) return "transparent";
        
        if (minValue === maxValue) return gradientColors[0]; 
    
        const clampedRatio = Math.max(0, Math.min(1, (count - minValue) / (maxValue - minValue)));
        const scaledIndex = clampedRatio * (gradientColors.length - 1);
    
        const index = Math.floor(scaledIndex);
        const nextIndex = Math.min(index + 1, gradientColors.length - 1);
        const factor = scaledIndex - index;
    
        return interpolateColor(gradientColors[index], gradientColors[nextIndex], factor);
    };
    
    const getSizeForCount = (count) => {
        const minSize = 40;
        const maxSize = 40;
        return scaleValue(count, minValue, maxValue, minSize, maxSize);
    };

    const getFontSizeForCount = (count) => {
        const minFontSize = 8;
        const maxFontSize = 14;
        return `${scaleValue(count, minValue, maxValue, minFontSize, maxFontSize)}px`;
    };

    const filteredDates = dates.filter(date => {
        return categories.some(category => getCountForCell(date, category) > 0);
    });

    if (loading) {
        return (
            <Spin
                className="spinner-by-center"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }
    if (chartData.length === 0) {
        return (
            <div className="no-data-container">
                <h3>No available data</h3>
            </div>
        );
    }

    return (
        <div className="volume-compare-table">
            <Scrollbars style={{ height: props.widget.layout.h * 35 - 70 }}>
                <table style={{ width: "100%", height: "100%" }}>
                    <thead className="sticky-header">
                    <tr>
                        <th style={{ border: "none" }}></th>
                        {filteredDates.map((date) => (
                            <th style={{ border: "none", fontSize: labelFontSize }} key={date}>{date}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {categories.map((category) => (
                        <tr key={category}>
                            <Tooltip title={category}>
                                <td style={{ border: "none", fontSize: labelFontSize,  }}
                                    className={classes.gridItemConcept}>{category}
                                </td>
                            </Tooltip>

                            {filteredDates.map((date) => (
                                <td key={`${category}-${date}`} className="bubble-cell">
                                    <Tooltip title={`${getCountForCell(date, category)}%`}>
                                        <div
                                            className="bubble"
                                            style={{
                                                width: `${getSizeForCount(getCountForCell(date, category))}px`,
                                                height: `${getSizeForCount(getCountForCell(date, category))}px`,
                                                backgroundColor: getCountForCell(date, category) > 0
                                                    ? getColorForCount(getCountForCell(date, category))
                                                    : "transparent",
                                                fontSize: getFontSizeForCount(getCountForCell(date, category)),
                                            }}
                                        >
                                            {getCountForCell(date, category) !== null &&
                                             getCountForCell(date, category) !== undefined && (
                                             <span>{getCountForCell(date, category)}</span>
                                            )}
                                        </div>
                                    </Tooltip>
                                </td>
                            ))}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </Scrollbars>
            <div className="indicator-container">
                <span className="indicator-label">{`${minValue}%`}</span>
                <div className="indicator-bar">
                    <div className="indicator-gradient"></div>
                </div>
                <span className="indicator-label">{`${maxValue}%`}</span>
            </div>
        </div>
    );
};
